import {
  ChakraFlex,
  ChakraGridItem,
  ChakraHStack,
  ChakraSimpleGrid,
  ChakraSpacer,
  ChakraText,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  Switch,
} from '@equip.health/ui';
import DiagnosisSearch from '~/components/common/DiagnosisSearch';
import DiagnosisListItem from '~/components/maud/patients/profile/clinical-profile/diagnosis/DiagnosisListItem';
import {
  DiagnosisStatusCode,
  filterSurveyDiagnosisCodesToBeAdded,
  getSortedDiagnosisList,
  hasPrimaryDiagnosis,
  isOpenDiagnosis,
} from '~/lib/util/clinicalProfile.util';
import EmptyDiagnosisList from '~/components/maud/patients/profile/clinical-profile/diagnosis/EmptyDiagnosisList';
import Loading from '~/pages/loading';
import DiagnosisList from '~/components/maud/patients/profile/clinical-profile/diagnosis/DiagnosisList';
import { getDiagnosesQuestionsResponseFromIntakeSurvey } from '~/lib/util/survey.util';
import { diagnosisSurveyMapping } from '~/lib/constants/diagnosisSurveyMapping.constants';
import { useMemo, useState } from 'react';
import {
  useGetDiagnosisCodesQuery,
  useGetPatientDiagnosesQuery,
} from '~/store/services/diagnoses';

const { labelBold } = EquipUITheme.typography;
const { neutral, secondary } = EquipUIFireflyV2Theme.colors;
const PATIENT_DIAGNOSIS_SECTION = 'patient__diagnosis-section';

type Props = {
  patientId: string;
  assessmentList: SurveyResponse[];
};
export const ClinicalProfileDiagnosisSection = ({
  patientId,
  assessmentList,
}: Props) => {
  const [isShowClosedDiagnosisChecked, setIsShowClosedDiagnosisChecked] =
    useState(false);
  const [diagnosisIdToEdit, setDiagnosisIdToEdit] = useState<string | null>(
    null,
  );
  const [foundDiagnosis, setFoundDiagnosis] = useState<PatientDiagnosis>(null);
  const [selectedDiagnosis, setSelectedDiagnosis] = useState<
    PatientDiagnosis | undefined
  >();

  const { data: patientDiagnoses } = useGetPatientDiagnosesQuery(patientId);

  // potential for this to use groupedAssessments API instead of assessmentsList
  const surveyDiagnosisList =
    getDiagnosesQuestionsResponseFromIntakeSurvey(assessmentList);

  const searchDiagnosisCodes: string[] = diagnosisSurveyMapping.reduce(
    (diagnosesCodes, diagnosisSurveyMap) => {
      if (surveyDiagnosisList.includes(diagnosisSurveyMap.value)) {
        diagnosesCodes.push(diagnosisSurveyMap.code);
      }
      return diagnosesCodes;
    },
    [],
  );

  const { data: diagnosesCodes, isLoading: isLoadingDiagnosesCodes } =
    useGetDiagnosisCodesQuery(searchDiagnosisCodes, {
      skip: !searchDiagnosisCodes.length,
    });

  const diagnosisList = useMemo<PatientDiagnosis[]>(() => {
    if (!patientDiagnoses) {
      return [];
    }

    if (diagnosesCodes) {
      const diagnosesResponseCodes = patientDiagnoses.map(
        ({ diagnosis: { code } }) => code,
      );

      const surveyDiagnoses = filterSurveyDiagnosisCodesToBeAdded(
        diagnosesCodes,
        diagnosesResponseCodes,
      );

      return [...surveyDiagnoses, ...(patientDiagnoses || [])];
    }

    return patientDiagnoses;
  }, [diagnosesCodes, patientDiagnoses]);

  const handleOnDiagnosisAdd = () => {
    setSelectedDiagnosis(null);
    if (foundDiagnosis && diagnosisIdToEdit === foundDiagnosis.externalId) {
      setDiagnosisIdToEdit(null);
    }
  };

  const diagnosisData = getSortedDiagnosisList(
    isShowClosedDiagnosisChecked
      ? diagnosisList
      : (diagnosisList ?? []).filter((diagnosis) =>
          isOpenDiagnosis(diagnosis?.statusCode as DiagnosisStatusCode),
        ),
  );
  const validatedDiagnosisHasCodeAlready = (
    diagnosisCode: string,
    updatedStatusCode: string,
    skipDiagnosisId?: string,
  ): boolean => {
    const diagnosis = diagnosisData.find(
      (d) =>
        d.diagnosis.code === diagnosisCode &&
        (d.statusCode === 'ACTIVE' || d.statusCode === 'PRELIMINARY') &&
        d.externalId !== skipDiagnosisId,
    );

    if (
      updatedStatusCode !== 'ACTIVE' &&
      updatedStatusCode !== 'PRELIMINARY' &&
      diagnosis
    ) {
      return false;
    }

    if (diagnosis) {
      setFoundDiagnosis(diagnosis);
      setDiagnosisIdToEdit(diagnosis.externalId);
      setSelectedDiagnosis(undefined);
      return true;
    }

    return false;
  };

  return (
    <ChakraSimpleGrid gap={4}>
      <ChakraGridItem>
        <ChakraFlex>
          <ChakraText
            color={secondary[50]}
            id={`${PATIENT_DIAGNOSIS_SECTION}__sub-header`}
            {...labelBold}
          >
            Diagnosis
          </ChakraText>
          <ChakraSpacer />
          <ChakraHStack
            alignItems="flex-start"
            borderRadius="16px"
            id={`${PATIENT_DIAGNOSIS_SECTION}__show-diagnosis`}
            marginRight="10px"
            spacing="16px"
          >
            <ChakraText color={neutral.secondary} {...labelBold}>
              Show closed diagnosis
            </ChakraText>
            <Switch
              isChecked={isShowClosedDiagnosisChecked}
              onChange={() => {
                setIsShowClosedDiagnosisChecked(!isShowClosedDiagnosisChecked);
              }}
              size="md"
            />
          </ChakraHStack>
        </ChakraFlex>
      </ChakraGridItem>
      <ChakraGridItem>
        <DiagnosisSearch
          id={PATIENT_DIAGNOSIS_SECTION}
          placeholder="Search here..."
          selected={[]}
          updateSelectedDiagnosis={(diagnosis: PatientDiagnosis) =>
            setSelectedDiagnosis(diagnosis)
          }
        />
      </ChakraGridItem>
      {selectedDiagnosis && (
        <DiagnosisListItem
          diagnosis={selectedDiagnosis}
          diagnosisIdToEdit={diagnosisIdToEdit ?? ''}
          isNewDiagnosis
          isPrimaryDiagnosisExists={hasPrimaryDiagnosis(
            diagnosisData,
            selectedDiagnosis,
          )}
          onAdd={handleOnDiagnosisAdd}
          onDiscard={() => setSelectedDiagnosis(undefined)}
          patientId={patientId}
          setDiagnosisIdToEdit={setDiagnosisIdToEdit}
          validatedDiagnosisHasCodeAlready={validatedDiagnosisHasCodeAlready}
        />
      )}
      {!hasPrimaryDiagnosis(diagnosisData || []) && <EmptyDiagnosisList />}
      {isLoadingDiagnosesCodes ? (
        <Loading />
      ) : (
        diagnosisData.length > 0 && (
          <DiagnosisList
            diagnosisIdToEdit={diagnosisIdToEdit ?? ''}
            diagnosisList={diagnosisData}
            onAdd={handleOnDiagnosisAdd}
            patientId={patientId}
            setDiagnosisIdToEdit={setDiagnosisIdToEdit}
            validatedDiagnosisHasCodeAlready={validatedDiagnosisHasCodeAlready}
          />
        )
      )}
    </ChakraSimpleGrid>
  );
};
